var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useEffect, useMemo, useState } from "react";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { formatCurrency } from "../utility/formatCurrency";
import { getDisplayedOrderTotal, toNumber } from "../utility/cartTotals";
import "../ui.css";
import "./orderSummary.css";
import { useCart, useCheckout } from "../../providers";
import { RadixModal } from "../primitives/RadixModal";
var toProperTitle = function (value) {
    return value.replace(/\w\S*/g, function (text) { return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase(); });
};
var toDiscountLines = function (value) {
    var discountList = [];
    if (Array.isArray(value)) {
        discountList = value;
    }
    else if (typeof value === "string") {
        try {
            var parsedDiscounts = JSON.parse(value);
            if (Array.isArray(parsedDiscounts)) {
                discountList = parsedDiscounts;
            }
        }
        catch (_error) {
            discountList = [];
        }
    }
    return discountList
        .map(function (entry) {
        if (!entry || typeof entry !== "object") {
            return null;
        }
        var discountEntry = entry;
        var rawCode = typeof discountEntry.code === "string" ? discountEntry.code.trim() : "";
        var amount = toNumber(discountEntry.amount);
        if (!rawCode || amount <= 0) {
            return null;
        }
        return {
            code: rawCode,
            amount: amount,
        };
    })
        .filter(function (line) { return Boolean(line); });
};
export var OrderSummary = function (_a) {
    var _b, _c, _d;
    var _e = _a.showFeeVerbiage, showFeeVerbiage = _e === void 0 ? true : _e, isCardPaymentSelectedOverride = _a.isCardPaymentSelectedOverride, _f = _a.ncaFeeDescription, ncaFeeDescription = _f === void 0 ? null : _f;
    var cart = useCart().cart;
    var isCardPaymentSelected = useCheckout().isCardPaymentSelected;
    var _g = useState(false), showTaxAndFeesModal = _g[0], setShowTaxAndFeesModal = _g[1];
    var store = (_b = window
        .session) === null || _b === void 0 ? void 0 : _b.store;
    var orderCart = cart;
    useEffect(function () {
        var isMounted = true;
        return function () {
            isMounted = false;
        };
    }, []);
    if (!cart) {
        return null;
    }
    var deliveryTip = toNumber(orderCart.delivery_tip);
    var deliveryFee = toNumber(orderCart.delivery_fee);
    var ncaFee = toNumber(orderCart.nca_fee);
    var serviceFee = toNumber(orderCart.service_fee);
    var hasDeliveryTip = deliveryTip > 0;
    var hasDeliveryFee = deliveryFee > 0;
    var effectiveIsCardPaymentSelected = typeof isCardPaymentSelectedOverride === "boolean"
        ? isCardPaymentSelectedOverride
        : isCardPaymentSelected;
    var hasNcaFee = ncaFee > 0 && effectiveIsCardPaymentSelected;
    var hasServiceFee = serviceFee > 0;
    var ncaTitle = (store === null || store === void 0 ? void 0 : store.nca_title) && store.nca_title.trim().length > 0
        ? toProperTitle(store.nca_title)
        : "Technology fee";
    var serviceFeeTitle = (store === null || store === void 0 ? void 0 : store.service_fee_title) && store.service_fee_title.trim().length > 0
        ? toProperTitle(store.service_fee_title)
        : "Service Fee";
    var total = getDisplayedOrderTotal(orderCart, effectiveIsCardPaymentSelected);
    var ncaVerbiage = (_c = store === null || store === void 0 ? void 0 : store.nca_verbiage) === null || _c === void 0 ? void 0 : _c.trim();
    var serviceFeeVerbiage = (_d = store === null || store === void 0 ? void 0 : store.service_fee_verbiage) === null || _d === void 0 ? void 0 : _d.trim();
    var taxBreakdownLines = useMemo(function () {
        var rawTaxes = orderCart.taxes;
        if (!Array.isArray(rawTaxes)) {
            return [{ label: "Tax", amount: toNumber(orderCart.tax) }].filter(function (line) { return line.amount > 0; });
        }
        var parsedTaxes = rawTaxes
            .map(function (entry) {
            var _a, _b, _c, _d;
            if (!entry || typeof entry !== "object") {
                return null;
            }
            var taxEntry = entry;
            var label = (_a = [taxEntry.name, taxEntry.title, taxEntry.label]
                .find(function (value) { return typeof value === "string" && value.trim().length > 0; })) !== null && _a !== void 0 ? _a : "Tax";
            var amount = toNumber((_d = (_c = (_b = taxEntry.amount) !== null && _b !== void 0 ? _b : taxEntry.value) !== null && _c !== void 0 ? _c : taxEntry.tax) !== null && _d !== void 0 ? _d : taxEntry.total);
            if (amount <= 0) {
                return null;
            }
            return {
                label: toProperTitle(label),
                amount: amount,
            };
        })
            .filter(function (line) { return Boolean(line); });
        if (parsedTaxes.length === 0) {
            return [{ label: "Tax", amount: toNumber(orderCart.tax) }].filter(function (line) { return line.amount > 0; });
        }
        return parsedTaxes;
    }, [orderCart.tax, orderCart.taxes]);
    var taxAndFeesBreakdown = __spreadArray(__spreadArray(__spreadArray([], taxBreakdownLines, true), (hasNcaFee ? [{ label: ncaTitle, amount: ncaFee, description: ncaFeeDescription }] : []), true), (hasServiceFee ? [{ label: serviceFeeTitle, amount: serviceFee }] : []), true);
    var discounts = toDiscountLines(orderCart.discounts);
    var taxesAndFeesTotal = taxAndFeesBreakdown.reduce(function (sum, line) { return sum + line.amount; }, 0);
    var totalSavings = discounts.reduce(function (sum, discount) { return sum + discount.amount; }, 0);
    var subtotal = toNumber(orderCart.subtotal);
    var subtotalWithDiscountAddedIn = (Math.round(subtotal * 100) + Math.round(totalSavings * 100)) / 100;
    return (React.createElement("section", { className: "radix-order-summary" },
        React.createElement("dl", { className: "radix-order-summary-list" },
            React.createElement("div", { className: "radix-order-summary-row" },
                React.createElement("dt", null, "Subtotal"),
                React.createElement("dd", null, formatCurrency(subtotalWithDiscountAddedIn))),
            totalSavings > 0 ? (React.createElement("div", { className: "radix-order-summary-row radix-order-summary-discount" },
                React.createElement("dt", null, "Discount"),
                React.createElement("dd", null,
                    "-",
                    formatCurrency(totalSavings)))) : null,
            hasDeliveryFee ? (React.createElement("div", { className: "radix-order-summary-row" },
                React.createElement("dt", null, "Delivery"),
                React.createElement("dd", null, formatCurrency(deliveryFee)))) : null,
            taxAndFeesBreakdown.length > 0 ? (React.createElement("div", { className: "radix-order-summary-row" },
                React.createElement("dt", null,
                    React.createElement("button", { type: "button", className: "radix-order-summary-label-with-action", "aria-label": "View taxes and other fees breakdown", onClick: function () { return setShowTaxAndFeesModal(true); } },
                        React.createElement("span", null, "Tax and other fees"),
                        React.createElement(InfoCircledIcon, { "aria-hidden": "true", className: "radix-order-summary-info-icon" }))),
                React.createElement("dd", null, formatCurrency(taxesAndFeesTotal)))) : null,
            hasDeliveryTip ? (React.createElement("div", { className: "radix-order-summary-row" },
                React.createElement("dt", null, "Gratuity"),
                React.createElement("dd", null, formatCurrency(deliveryTip)))) : null,
            React.createElement("div", { className: "radix-order-summary-row radix-order-summary-total" },
                React.createElement("dt", null, "Total"),
                React.createElement("dd", null, formatCurrency(total)))),
        React.createElement(RadixModal, { open: showTaxAndFeesModal, onOpenChange: setShowTaxAndFeesModal, title: "Tax and other fees", description: "Here is the full breakdown of all taxes and fees applied to your order." },
            React.createElement("dl", { className: "radix-order-summary-breakdown-list" }, taxAndFeesBreakdown.map(function (line) { return (React.createElement("div", { className: "radix-order-summary-breakdown-item", key: "".concat(line.label, "-").concat(line.amount) },
                React.createElement("div", { className: "radix-order-summary-row" },
                    React.createElement("dt", null, line.label),
                    React.createElement("dd", null, formatCurrency(line.amount))),
                line.description ? (React.createElement("small", { className: "radix-order-summary-breakdown-description" }, line.description)) : null)); })))));
};
